import axios, { AxiosRequestConfig, AxiosResponse, CancelToken, Method } from "axios";
import { renewAccessToken, deleteSession } from "store/actions/session";
import { errorAlert } from "utils/alerts";
import store from "store";
import { renewRole } from 'store/actions/session';
import { CHANGE_MODAL_STATE } from "store/reducers/newVersionModal/modal/types"

const API_URL: string | undefined = process.env.REACT_APP_API_URL;
//const MANAGER_PORTAL_URL: string | undefined = process.env.REACT_APP_MANAGER_PORTAL_URL;
//const NODE_ENV: string | undefined = process.env.NODE_ENV;

let isAlreadyFetchingAccessToken: boolean = false;
let subscribers: Array<(accessToken: string) => void> = [];

type RequestOptions = {
  params?: unknown;
  data?: unknown;
  [key: string]: unknown;
};

type AxiosCustomRequestConfig = AxiosRequestConfig & {
  authenticated?: boolean;
};

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 2 * 60 * 1000,
  headers: {
    "content-type": "application/json"
  },
});

axiosInstance.interceptors.request.use(
  (config: AxiosCustomRequestConfig) => {
    const token: string | null = localStorage.getItem("accessToken");

    if (config.authenticated && token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.headers.version && response.headers.version !== "undefined" && response.headers.version !== process.env.REACT_APP_VERSION) {
      store.dispatch({ type: CHANGE_MODAL_STATE, isOpen: true });
    }
    return response;
  },
  (error) => {
    const originalRequest = { ...error.config };

    if (error.response && error.response.status === 401 && originalRequest._retry !== true && originalRequest.authenticated) {
      originalRequest._retry = true;

      const retryOriginalRequest = new Promise(resolve => {
        addSubscriber((accessToken: string) => {
          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          error.response.config.headers["Authorization"] = `Bearer ${accessToken}`;

          resolve(axiosInstance.request(originalRequest));
        })
      });

      if (!isAlreadyFetchingAccessToken) {
        isAlreadyFetchingAccessToken = true;

        return store
          .dispatch(renewAccessToken())
          .then(() => {
            const token: string = localStorage.getItem("accessToken") as string;
            isAlreadyFetchingAccessToken = false;
            onAccessTokenFetched(token);

            return retryOriginalRequest;
          })
          .catch(() => {
            store.dispatch(deleteSession());
            isAlreadyFetchingAccessToken = false;
          });
      } else {
        return retryOriginalRequest;
      }
    }

    if (error.response && error.response.status === 403) {
      store.dispatch(deleteSession());
    }

    if (error.response && error.response.status === 405) {
      store.dispatch(renewRole());
    }

    return Promise.reject(error);
  }
);

export async function apiRequest(
  method: Method,
  path: string,
  options: RequestOptions = {},
  authenticated: boolean = true,
  onUploadProgress?: (progressEvent: any) => void,
  cancelToken?: CancelToken
): Promise<AxiosResponse | null> {
  try {
    return await axiosInstance.request({
      method: method,
      url: `/${path}`,
      authenticated,
      responseType: options.responseType,
      params: options.params,
      data: options.data || {},
      onUploadProgress: onUploadProgress,
      cancelToken: cancelToken
    } as AxiosCustomRequestConfig);
  } catch (error: any) {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        if (isAlreadyFetchingAccessToken) {
          return Promise.resolve(null);
        }
      }

      if (status !== 400 && status !== 403 && status !== 405) {
        if (typeof data.error === "string" && data.error) {
          if (data.error !== "Wrong number of segments") {
            errorAlert(data.error);
          }
        }

        if (typeof data.error === "object" && data.error.message) {
          errorAlert(data.error.message);
        }
      }
    }
    return Promise.reject(error.response);
  }
}

window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  console.warn(`UNHANDLED PROMISE REJECTION: ${event.reason}`);
};

export const redirectToAuth = (): void => {
  document.location.href = '/login';
};

const onAccessTokenFetched = (accessToken: string) => {
  subscribers.forEach(callback => callback(accessToken));
  subscribers = [];
};

function addSubscriber(callback: (accessToken: string) => void) {
  subscribers.push(callback);
};